import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useClassName, useWindowSize } from 'common/hooks';
import { Link } from 'react-router-dom';

import './park-screen-breadcrumbs.less';
import { DateTime } from 'luxon';
import { OperationalHoursModal } from '../OperationalHoursModal';
import { TicketOptionsModal } from 'public/components/AttractionScreen/TicketOptionsModal';
import { inject } from 'mobx-react';
import { getContentfulField } from 'common/components';
import {
  categories,
  getCategoryByCta,
  standardizedTrack,
} from 'utils/analytics';
import { formatOpeningHoursForDate } from 'utils/helpers/openingHours';
import { scrollTo } from 'utils/helpers/scroll';

const ScrollDirectionEnum = {
  UP: 'up',
  DOWN: 'down',
  NONE: null,
};

const ParkScreenBreadcrumb = ({
  links,
  className,
  featuredFlags,
  openingHours,
  isDateUnavailable,
  openingHoursBanner,
  venueParkAccessMessage,
  ticketOptions,
  betaFeatures,
  ridesCards,
  venueSlug = '',
  venueId,
  emptyAsset,
  emptyAssetStyle,
}) => {
  const classNames = useClassName('ParkScreenBreadcrumb');

  const lastScrollYRef = useRef(0);
  const [isVisible, setIsVisible] = useState(true);
  const [date, setDate] = useState(DateTime.local());
  const [openHoursModal, setOpenHoursModal] = useState(false);
  const [isParkEntryEnabled, setIsParkEntryEnabled] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const parkAccessMessage = getContentfulField(venueParkAccessMessage);
  const { isDesktop, width } = useWindowSize();

  useEffect(() => {
    const getParkEntryEnabled = async () => {
      const isParkEntryEnabled = await betaFeatures.fetch('Park Entry Pass');
      setIsParkEntryEnabled(isParkEntryEnabled);
    };
    getParkEntryEnabled();
  }, []);

  const navigationHeaderRef = useRef(null);
  const breadcrumbRef = useRef(null);
  const getTicketsContainerRef = useRef(null);

  useEffect(() => {
    const findElements = () => {
      if (!navigationHeaderRef.current) {
        navigationHeaderRef.current = document.querySelector(
          '.navigation-header__menu-container'
        );
      }
      if (!breadcrumbRef.current) {
        breadcrumbRef.current = document.querySelector(
          '.park-screen-breadcrumb__container'
        );
      }
      if (!getTicketsContainerRef.current) {
        getTicketsContainerRef.current = document.querySelector(
          '.get-tickets__container'
        );
      }
    };

    findElements();

    const timeoutId = setTimeout(() => {
      findElements();
    }, 100);

    const handleScroll = () => {
      findElements();

      if (!navigationHeaderRef.current || !breadcrumbRef.current) {
        return;
      }

      const currentScrollY = window.scrollY;
      const prev = lastScrollYRef.current;
      const dir =
        currentScrollY > prev
          ? ScrollDirectionEnum.DOWN
          : currentScrollY < prev
          ? ScrollDirectionEnum.UP
          : ScrollDirectionEnum.NONE;

      const navigationHeaderRect =
        navigationHeaderRef.current.getBoundingClientRect();
      const navigationHeaderBottom = navigationHeaderRect.bottom;
      const offset = isDesktop ? 16 : 0;

      // Si el navigation header aún está visible (no ha desaparecido completamente)
      if (navigationHeaderBottom > 0) {
        // El breadcrumb sigue al navigation header
        const topPosition = navigationHeaderBottom + offset;
        breadcrumbRef.current.style.top = `${topPosition}px`;

        // Manejar visibilidad y opacidad del header según dirección
        if (dir === ScrollDirectionEnum.DOWN) {
          setIsVisible(true); // Breadcrumb visible cuando scroll down
          navigationHeaderRef.current.style.opacity = 1;
        } else if (dir === ScrollDirectionEnum.UP) {
          setIsVisible(false);
          navigationHeaderRef.current.style.opacity = 1;
        }
      } else {
        // El navigation header ya desapareció, breadcrumb se queda fijo
        breadcrumbRef.current.style.top = `${offset}px`;

        if (dir === ScrollDirectionEnum.DOWN) {
          setIsVisible(true); // Breadcrumb desaparece cuando scroll down
          navigationHeaderRef.current.style.opacity = 0;
        } else if (dir === ScrollDirectionEnum.UP) {
          setIsVisible(false);
          navigationHeaderRef.current.style.opacity = 1;
        }
      }

      lastScrollYRef.current = currentScrollY;

      //expand or collapse the get tickets container
      if (getTicketsContainerRef.current) {
        const container = getTicketsContainerRef.current;
        const offsetTop = container.offsetTop;
        const clientHeight = container.clientHeight;
        const totalTop = clientHeight + offsetTop;
        setIsExpanded(currentScrollY > totalTop);
      }
    };

    // Inicializar posición del breadcrumb con reintentos
    const initializeBreadcrumbPosition = () => {
      if (navigationHeaderRef.current && breadcrumbRef.current) {
        const navigationHeaderRect =
          navigationHeaderRef.current.getBoundingClientRect();
        const navigationHeaderBottom = navigationHeaderRect.bottom;
        const offset = isDesktop ? 16 : 0;
        const topPosition = navigationHeaderBottom + offset;
        breadcrumbRef.current.style.top = `${topPosition}px`;
        return true; // Indica que se ejecutó exitosamente
      }
      return false; // No se pudo ejecutar
    };

    // Reintentar múltiples veces hasta que los elementos estén disponibles
    let retryCount = 0;
    const maxRetries = 10;

    const retryInitialize = () => {
      const success = initializeBreadcrumbPosition();

      if (!success && retryCount < maxRetries) {
        retryCount++;
        requestAnimationFrame(retryInitialize);
      }
    };

    // Ejecutar inmediatamente y reintentar si es necesario
    retryInitialize();

    // También intentar en los siguientes frames y timeouts por si acaso
    setTimeout(() => retryInitialize(), 100);
    setTimeout(() => retryInitialize(), 300);
    setTimeout(() => retryInitialize(), 500);

    window.addEventListener('scroll', handleScroll);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [width, isDesktop]);

  const trackingLabel = window.location.pathname; //We save this here to make sure the window location is not overwritten before sending the track events

  const handleClick = (heroCtaLink, heroCtaText) => {
    const category = getCategoryByCta(heroCtaLink) || categories.ERC_INFO;

    standardizedTrack(
      'click',
      'button',
      'hero',
      heroCtaText,
      category,
      trackingLabel
    );
  };
  const getLabel = () => {
    const comingSoon = featuredFlags.includes('Coming Soon');

    if (comingSoon) return 'Coming Soon';

    return formatOpeningHoursForDate(openingHours, date);
  };

  useEffect(() => {
    const HEADER_OFFSET = 48;

    const handler = () => {
      const links = Array.from(
        document.getElementsByClassName(
          'park-screen-breadcrumb__container__item'
        )
      );

      if (links.length === 0) return;

      links.forEach((link) => {
        link.classList.remove('active');
      });

      const sections = links
        .map((link) => {
          const href = link.getAttribute('href');
          if (!href || !href.includes('#')) return null;

          const sectionId = href.split('#')[1];
          const section = document.getElementById(sectionId);
          if (!section) return null;

          const rect = section.getBoundingClientRect();
          return {
            link,
            sectionId,
            rect,
          };
        })
        .filter(Boolean);

      let bestSection = null;
      let maxScore = -1;

      sections.forEach(({ link, rect }) => {
        const viewportHeight = window.innerHeight;

        const ACTIVATION_THRESHOLD = 80;
        if (rect.top > HEADER_OFFSET + ACTIVATION_THRESHOLD) {
          return;
        }

        if (rect.bottom < HEADER_OFFSET) {
          return;
        }

        const visibleTop = Math.max(HEADER_OFFSET, rect.top);
        const visibleBottom = Math.min(viewportHeight, rect.bottom);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);

        if (visibleHeight <= 0) return;

        const distanceFromIdeal = Math.max(0, rect.top - HEADER_OFFSET);
        const score = visibleHeight * 1000 - distanceFromIdeal * 10;

        if (score > maxScore) {
          maxScore = score;
          bestSection = link;
        }
      });

      if (bestSection) {
        bestSection.classList.add('active');
      }
    };

    handler();
    setTimeout(handler, 100);

    document.addEventListener('scroll', handler, { passive: true });

    return () => {
      document.removeEventListener('scroll', handler);
    };
  }, []);

  useEffect(() => {
    const defaultLinks = document.getElementsByClassName(
      'park-screen-breadcrumb__container__item--default'
    );

    const handler = (e, link) => {
      e.preventDefault();
      e.stopPropagation();
      const href = link.getAttribute('href');
      if (href && href.includes('#')) {
        const hash = href.split('#')[1];
        if (hash) {
          const targetElement = document.getElementById(hash);
          if (targetElement) {
            scrollTo(targetElement, -48);
          }
        }
      }
    };
    Array.from(defaultLinks).forEach((link) => {
      link.addEventListener('click', (e) => handler(e, link));
    });
    return () => {
      Array.from(defaultLinks).forEach((link) => {
        link.removeEventListener('click', (e) => handler(e, link));
      });
    };
  }, []);

  const renderDefaultLink = (link) => {
    return (
      <Link className={classNames('container__item', 'default')} to={link.url}>
        {link.icon && <img src={link.icon.assetUrl} alt={link.title} />}
        {link.title}
      </Link>
    );
  };

  const getDate = () => {
    const today = DateTime.local();
    return today.toFormat('yyyy-MM-dd') === date.toFormat('yyyy-MM-dd')
      ? 'Today'
      : date.toFormat('LLL dd');
  };

  const renderOpenHoursLink = (link) => {
    return (
      <div className={classNames('container__item', 'open-hours')}>
        <div className={classNames('container__item--open-hours__text')}>
          <p className={classNames('container__item--open-hours__text__date')}>
            {getDate()}
          </p>
          <p className={classNames('container__item--open-hours__text__label')}>
            {getLabel()}
          </p>
        </div>
        <div className={classNames('container__item--open-hours__link')}>
          <OperationalHoursModal
            isDateUnavailable={isDateUnavailable}
            open={openHoursModal}
            openingHours={openingHours}
            trigger={
              <div
                className={classNames(
                  'container__item--open-hours__link__text'
                )}
                onClick={() => setOpenHoursModal(true)}>
                {link.title}
              </div>
            }
            onOpen={() => setOpenHoursModal(true)}
            onClose={() => setOpenHoursModal(false)}
            initialDate={date.toISO()}
            updateSelectedDate={(jsDate) => {
              if (jsDate instanceof Date) {
                setDate(DateTime.fromJSDate(jsDate));
              }
            }}
            openingHoursBanner={openingHoursBanner}
            ridesCards={ridesCards}
            venueSlug={venueSlug}
            venueId={venueId}
            emptyAsset={emptyAsset}
            emptyAssetStyle={emptyAssetStyle}
          />
        </div>
      </div>
    );
  };

  const renderButtonTicketLink = (link) => {
    return (
      <TicketOptionsModal
        trigger={
          <div className={classNames('container__item', 'button-ticket')}>
            {link.title}
          </div>
        }
        parkAccessMessage={isParkEntryEnabled ? parkAccessMessage : null}
        options={ticketOptions}
        venueSlug={venueSlug}
        onClick={handleClick}
      />
    );
  };

  return (
    <>
      <div
        className={`${classNames(
          'container',
          isVisible ? 'visible' : 'hidden'
        )} ${className} ${isExpanded ? 'expanded' : ''}`}>
        <ul>
          {links.map((link, index) => {
            return (
              <li key={`park-screen-breadcrumb-item-${index}`}>
                {link.type === 'default' && renderDefaultLink(link)}
                {link.type === 'open-hours' && renderOpenHoursLink(link)}
                {link.type === 'button-ticket' && renderButtonTicketLink(link)}
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
};

ParkScreenBreadcrumb.propTypes = {
  links: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default inject('betaFeatures')(ParkScreenBreadcrumb);
